import { useFormikContext } from 'formik'
import { AddABookingValues } from 'helpers/generateInitialValuesForActivity'
import { SchedulablePresenter } from 'shared/presenters/SchedulablePresenter'
import { DependencyList, useEffect } from 'react'
import dayjs from 'dayjs'
import useDidMountEffect from 'shared/state/useDidMountEffect'

export interface useActivityValuesProps {
  activity: SchedulablePresenter
  availableDatesForCalendar: Date[]
  loadingData?: boolean
}

/**
 * useEffects for the ActivityConfig view
 */
const useActivityValues = ({
  availableDatesForCalendar,
  loadingData = false,
  activity
}: useActivityValuesProps): AddABookingValues => {
  const { values, initialValues, setFieldValue } =
    useFormikContext<AddABookingValues>()

  const { adultsGoing, childrenGoing, isGift, calendarSelection } = values

  const peopleGoing = adultsGoing + childrenGoing

  const resetCalendar = () => setFieldValue('calendarSelection', undefined)
  const resetExclusive = () => setFieldValue('exclusive', false)

  /**
   * If the purchase is marked as a gift, we need to reset the
   * timeslot and calendar form values, as these are no longer relevant.
   */
  useEffect(() => {
    if (isGift) {
      setFieldValue('calendarSelection', initialValues.calendarSelection)
      setFieldValue('timeSlot', initialValues.timeSlot)
    }
    // TODO #6362 - Fix ESLint - hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGift])

  // when number of people going is reduced below minimum, uncheck exclusive checkbox
  useEffect(() => {
    onPeopleGoingChange({
      hasEnoughForExclusive:
        activity.hasEnoughPeopleGoingForExclusive(peopleGoing),
      minCapacity: activity.minimum_capacity,
      privateActivity: activity.private_activity,
      peopleGoing,
      resetExclusive,
      resetCalendar
    })
    // TODO #6362 - Fix ESLint - hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peopleGoing])
  /**
   * reset the calendar selection if people going is not selected
   *
   * the calendar data is filtered based on people going. If we raise the people going we need
   * to identify if the date selected in the calendar is useful anymore. If not, remove it
   */
  useResetCalendarSelectionIfNotAvailable(
    [peopleGoing, availableDatesForCalendar],
    calendarSelection,
    availableDatesForCalendar,
    loadingData,
    resetCalendar
  )
  return values
}

export default useActivityValues

const useResetCalendarSelectionIfNotAvailable = (
  deps: DependencyList,
  calendarSelection: AddABookingValues['calendarSelection'],
  calendarData: useActivityValuesProps['availableDatesForCalendar'],
  loadingData: useActivityValuesProps['loadingData'],
  isUnavailableCallback: () => void
) => {
  useDidMountEffect(() => {
    // we do not need to reset if there is no date to check for
    if (!calendarSelection) {
      return
    }

    // data is temporarily undefined when fetching and this can throw off below conditionals
    if (loadingData) {
      return
    }

    // check it see if calendar date is available, if so do not need to reset
    if (isCalendarSelectionAvailable(calendarSelection, calendarData)) {
      return
    }

    isUnavailableCallback()
  }, deps)
}

const isCalendarSelectionAvailable = (
  calendarSelection: AddABookingValues['calendarSelection'],
  availableDates: useActivityValuesProps['availableDatesForCalendar']
) => {
  if (!availableDates.length) {
    return false
  }

  const selectionIsAvailable = !!availableDates?.filter(date => {
    return calendarSelection?.isSame(dayjs(date), 'day')
  }).length

  return selectionIsAvailable
}

export const onPeopleGoingChange = ({
  hasEnoughForExclusive,
  minCapacity,
  privateActivity,
  peopleGoing,
  resetExclusive,
  resetCalendar
}: {
  hasEnoughForExclusive: boolean
  minCapacity?: number | null
  privateActivity?: boolean | null
  peopleGoing: number
  resetExclusive: () => void
  resetCalendar: () => void
}) => {
  /**
   * prevent people making an exclusive booking if they under minimum capacity
   * we do this by unchecking the private booking to fallback to group booking if they fall under necessary min capacity
   */
  if (!hasEnoughForExclusive) {
    resetExclusive()
  }

  /**
   * We want to reset the calendar to prevent people from making private bookings under min capacity
   */
  if (privateActivity && minCapacity && peopleGoing < minCapacity) {
    resetCalendar()
  }
}
